import type { ExistingAsset, ValueOverviewTenantSummary } from '@einwert/valuation-api-client'
import type {
    CustomerExistingEinwertValuation as ExistingEinwertValuation,
    CustomerExistingExternalValuation as ExistingExternalValuation,
    CustomerExistingHistoricalValuation as ExistingHistoricalValuation,
} from '@einwert/valuation-api-client'
import { isValueOverviewTenantSummaryItem } from 'ClientPortal/src/api/valuationModels'
import { MonetaryValue, TENANT_USAGE_TYPE_LABELS, TenantOverviewBreakdown } from 'ComponentLibrary'

export const getUsageTypeSplit = (summary?: ValueOverviewTenantSummary, getUsageTypeLabel?: (usageType: string) => string) => {
    const rentalIncomeSummary = Object.values(summary || {})
    return rentalIncomeSummary
        .filter(isValueOverviewTenantSummaryItem)
        .map((summary) => ({
            type: summary.type,
            label: getUsageTypeLabel?.(summary.type) ?? TENANT_USAGE_TYPE_LABELS[summary.type] ?? TENANT_USAGE_TYPE_LABELS.other,
            value: MonetaryValue.fromMonetaryValueDataObject(summary.gross_rental_income?.total).toDecimal(),
        }))
        .filter(({ type }) => type !== 'total')
}

export const getTenantBreakdown = (summary?: ValueOverviewTenantSummary): TenantOverviewBreakdown[] =>
    Object.values(summary || {})
        .filter(isValueOverviewTenantSummaryItem)
        .map((summary) => ({
            usage_type: summary.type,
            number_of_units: summary.number_of_units,
            total_area: summary.total_area,
            vacancy: {
                percentage: summary.vacancy_percentage,
                area: summary.vacancy_area,
                units: summary.vacancy_number_of_units,
            },
            rent: summary.gross_rental_income,
            actual_rent: summary.actual_rental_income,
            costs: {
                management: summary.management_cost,
            },
            net_income: summary.net_rental_income,
        }))

export const getValuationStructuresWithIndex = (
    asset?: ExistingAsset | null,
    valuation?: ExistingEinwertValuation | ExistingExternalValuation | ExistingHistoricalValuation | null
) => {
    const assetStructures = asset?.economic_units.flatMap((economicUnit) => economicUnit.structures) || []
    const valuationStructures = valuation?.economic_units.flatMap((economicUnit) => economicUnit.structures) || []
    return assetStructures
        .map((structure, index) => ({
            ...structure,
            fictional_construction_year: valuationStructures.find(
                (valuationStructure) => valuationStructure.structure_uuid === structure.uuid
            )?.life_span.fictional_construction_year,
            assetStructureIndex: index,
            valuationStructureIndex: valuationStructures.findIndex(
                (valuationStructure) => valuationStructure.structure_uuid === structure.uuid
            ),
        }))
        .filter(({ valuationStructureIndex }) => !valuation || valuationStructureIndex !== -1)
}
